import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { VideoExperienceTile } from '../video-experience-tile';
import { AudioExperienceTile } from '../audio-experience-tile';
import { Experience, Content } from '../../hooks/api/useExperiences';
import { addVimeoMetadataToExperiences, fetchVimeoMetadataBatch } from '../../hooks/api/useBrowseExperiences';
import { ROUTE_PATH } from '../../routes/route-paths';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';

interface FiStoryResultsProps {
  items: Record<string, unknown>[];
  searchQuery: string;
  onEngage?: (contentId: number) => void;
  /**
   * Optional section title override. Defaults to ``Stories from others``
   * (Fi's canonical copy). Pass a peer-specific string like
   * ``More from <peer>`` when reusing this widget for Peersona's
   * referral_followup carousel — same visual treatment, different framing.
   */
  title?: string;
  /**
   * Hide the "See more results like this" footer CTA. Defaults to ``true``
   * (Fi's existing behavior). Pass ``false`` for surfaces where the
   * carousel is already a closed set the user has no reason to expand
   * (e.g. Peersona's "More from <peer>" — the carousel IS all the peer's
   * relevant audios; there's no "broader topic" search to escape to).
   */
  showSeeMore?: boolean;
}

const asExperience = (raw: Record<string, unknown>): (Experience | Content) | null => {
  if (typeof raw.id !== 'number') return null;
  return raw as unknown as Experience | Content;
};

function CarouselNavChevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden className="text-gray-800">
      {dir === 'left' ? (
        <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}

export const FiStoryResults: React.FC<FiStoryResultsProps> = ({
  items,
  searchQuery,
  onEngage,
  title = 'Stories from others',
  showSeeMore = true,
}) => {
  const history = useHistory();
  const isMdUp = useMediaQuery('md');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [enrichedItems, setEnrichedItems] = useState(items);
  const showNav = isMdUp && enrichedItems.length >= 3;

  useEffect(() => {
    setEnrichedItems(items);
  }, [items]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const experiences = items.map((raw) => asExperience(raw)).filter((e): e is Experience | Content => e != null);
      const needsVimeo = experiences.filter((exp) => exp.video_id && !(exp as Content).vimeo_thumbnail_url);
      if (!needsVimeo.length) return;

      const videoIds = needsVimeo.map((exp) => exp.video_id).filter(Boolean) as string[];
      const metadataMap = await fetchVimeoMetadataBatch(videoIds);
      if (cancelled) return;

      const byId = new Map(experiences.map((e) => [e.id, e]));
      needsVimeo.forEach((exp) => {
        const updated = addVimeoMetadataToExperiences(exp as Content, metadataMap);
        byId.set(exp.id, updated);
      });

      setEnrichedItems(
        items.map((raw) => {
          const id = raw.id;
          if (typeof id !== 'number' || !byId.has(id)) return raw;
          return { ...raw, ...byId.get(id) } as Record<string, unknown>;
        }),
      );
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [items]);

  const seeAll = () => {
    const q = encodeURIComponent(searchQuery || '');
    history.push(`${ROUTE_PATH.BROWSE_STORIES}?search=${q}`);
  };

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft;
    const max = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(left > 0);
    setCanScrollRight(left < max - 1);
  }, []);

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
    window.requestAnimationFrame(updateScrollState);
  };

  useEffect(() => {
    updateScrollState();
  }, [enrichedItems.length, updateScrollState]);

  const querySubtitle = useMemo(() => {
    const q = searchQuery?.trim();
    return q ? `Results for “${q}”` : null;
  }, [searchQuery]);

  return (
    <div className="bg-button-secondary w-full rounded-[20px] px-5 py-[18px]">
      <div className="mb-3 flex min-h-[36px] flex-col justify-center gap-0.5">
        <div className="flex items-center justify-between gap-6">
          <p className="font-albert-sans shrink-0 text-xl font-semibold leading-[1.25] text-black">{title}</p>
          {showNav ? (
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                aria-label="Scroll left"
                onClick={() => scrollBy(-220)}
                disabled={!canScrollLeft}
                className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-[0px_1px_0.5px_rgba(29,41,61,0.02)] hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40"
              >
                <CarouselNavChevron dir="left" />
              </button>
              <button
                type="button"
                aria-label="Scroll right"
                onClick={() => scrollBy(220)}
                disabled={!canScrollRight}
                className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-[0px_1px_0.5px_rgba(29,41,61,0.02)] hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40"
              >
                <CarouselNavChevron dir="right" />
              </button>
            </div>
          ) : null}
        </div>
        {querySubtitle ? <p className="font-open-sans text-xs text-gray-600">{querySubtitle}</p> : null}
      </div>
      <div
        ref={scrollRef}
        onScroll={updateScrollState}
        className="flex min-h-0 gap-5 overflow-x-auto overflow-y-clip pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {enrichedItems.map((raw, i) => {
          const exp = asExperience(raw);
          if (!exp) return null;
          const vid = (raw as { video_id?: unknown }).video_id;
          const isVideo = raw.media_type === 'VIDEO' || (!raw.media_type && raw.video_id) || Boolean(vid);
          const engage = () => onEngage?.(exp.id);
          return (
            <div key={exp.id ?? i} className="w-[194px] shrink-0">
              {isVideo ? (
                <VideoExperienceTile experience={exp} onTileEngage={engage} showTopicTag={false} />
              ) : (
                <AudioExperienceTile experience={exp} onTileEngage={engage} showTopicTag={false} />
              )}
            </div>
          );
        })}
      </div>
      {showSeeMore ? (
        <div className="mt-8 flex w-full max-w-[408px] flex-col items-center gap-2 self-center">
          <button
            type="button"
            onClick={seeAll}
            className="font-albert-sans border-input-border-gray text-primary w-full max-w-[346px] rounded-full border bg-white px-7 py-2.5 text-base font-medium leading-5 shadow-[0px_1px_0.5px_rgba(29,41,61,0.02)] transition-colors hover:bg-gray-50"
          >
            See more results like this
          </button>
        </div>
      ) : null}
    </div>
  );
};
